import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { Route as rootRoute } from './__root';
import { Layout } from '@/client/layouts/layout';
import { useAllPersistentStreamsStore, type PersistentStreamWithClient } from '@/client/store/allPersistentStreamsStore';
import { useEncoderWSSStore, type EncoderWSSRecord } from '@/client/store/encoderWSSStore';
import { useClientsStore } from '@/client/store/clientsStore';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Input } from '@/client/components/ui/input';
import { Switch } from '@/client/components/ui/switch';
import { Label } from '@/client/components/ui/label';
import { Button } from '@/client/components/ui/button';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { RefreshCw, X, Download } from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import { formatBitrate, formatFrameRate, formatResolution } from '@/client/lib/format';
import { downloadCSV } from '@/client/lib/csv';
import { parseString, parseBoolean, buildSearchParams } from '@/client/lib/searchParams';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';
import { SortableHeader, LiveStatus, LastCommunication } from '@/client/components/table-utils';
import { ClientNameCell } from '@/client/components/tables/cells';
import type { EncoderStreamInfo } from '@shared/types';

type PersistentStreamsSearch = {
  clientID?: string;
  search?: string;
  onlineOnly?: boolean;
};

/** Persistent stream data merged with real-time WSS data */
interface PersistentStreamWithWSS extends PersistentStreamWithClient {
  wss: EncoderWSSRecord | null;
}

/** Get stream info, preferring WSS data over DB data */
function getStreamInfo(stream: PersistentStreamWithWSS): EncoderStreamInfo | null {
  // WSS provides raw streamInfo with real_frame_rate, DB provides pre-parsed EncoderStreamInfo
  if (stream.wss?.streamInfo) {
    return {
      width: stream.wss.streamInfo.width,
      height: stream.wss.streamInfo.height,
      bitrate: stream.wss.streamInfo.bitrate,
      frameRate: stream.wss.streamInfo.real_frame_rate,
      aspectRatio: stream.wss.streamInfo.displayAspectRatio,
      hasCaptions: stream.wss.streamInfo.inputHasCaptions,
    };
  }
  return stream.streamInfo ?? null;
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'persistent-streams',
  validateSearch: (search: Record<string, unknown>): PersistentStreamsSearch => ({
    clientID: parseString(search.clientID),
    search: parseString(search.search),
    onlineOnly: parseBoolean(search.onlineOnly, true), // default: true
  }),
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: PersistentStreamsComponent,
});

const columnHelper = createColumnHelper<PersistentStreamWithWSS>();

const columns = [
  columnHelper.accessor('clientName', {
    header: ({ column }) => <SortableHeader column={column}>Client</SortableHeader>,
    cell: (info) => {
      const clientName = info.getValue();
      if (!clientName) return '-';
      return <ClientNameCell clientID={info.row.original.clientID} clientName={clientName} />;
    },
  }),
  columnHelper.accessor('title', {
    header: ({ column }) => <SortableHeader column={column}>Title</SortableHeader>,
    cell: (info) => info.getValue() || info.row.original.encoderID,
  }),
  columnHelper.display({
    id: 'liveStatus',
    header: 'Status',
    cell: ({ row }) => <LiveStatus wss={row.original.wss} dbStatus={row.original.status} />,
  }),
  columnHelper.display({
    id: 'resolution',
    header: 'Resolution',
    cell: ({ row }) => {
      const streamInfo = getStreamInfo(row.original);
      if (!streamInfo?.width || !streamInfo?.height) return <span className="text-muted-foreground">-</span>;
      return formatResolution(streamInfo.width, streamInfo.height);
    },
  }),
  columnHelper.display({
    id: 'bitrate',
    header: 'Bitrate',
    cell: ({ row }) => {
      const streamInfo = getStreamInfo(row.original);
      if (!streamInfo?.bitrate) return <span className="text-muted-foreground">-</span>;
      return formatBitrate(streamInfo.bitrate);
    },
  }),
  columnHelper.display({
    id: 'frameRate',
    header: 'FPS',
    cell: ({ row }) => {
      const streamInfo = getStreamInfo(row.original);
      if (!streamInfo?.frameRate) return <span className="text-muted-foreground">-</span>;
      return formatFrameRate(streamInfo.frameRate);
    },
  }),
  columnHelper.accessor('LiveStreamURI', {
    header: 'Live Stream URI',
    cell: (info) => {
      const value = info.getValue();
      return value ? <span className="font-mono text-xs">{value}</span> : '-';
    },
    enableSorting: false,
  }),
  columnHelper.accessor('lastCommunication', {
    header: ({ column }) => <SortableHeader column={column}>Last Comm</SortableHeader>,
    cell: (info) => <LastCommunication lastCommunication={info.getValue()} />,
  }),
];

function PersistentStreamsComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const {
    clientID,
    search: searchFilter = '',
    onlineOnly: showOnlineOnly = true 
  } = useSearch({ from: '/persistent-streams' });

  // Persist search params across navigation
  usePersistedSearch('/persistent-streams', { clientID: undefined, search: undefined, onlineOnly: true });

  const { streams, loading, error, cacheInfo, fetchStreams, invalidateCache } = useAllPersistentStreamsStore();
  const wssEncoders = useEncoderWSSStore((state) => state.encoders);
  const { clients, fetchClients } = useClientsStore();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'clientName', desc: false }]);

  // Update URL when search filter changes
  const setSearchFilter = (value: string) => {
    navigate({
      to: '/persistent-streams',
      search: buildSearchParams({ clientID, search: value || undefined, onlineOnly: showOnlineOnly ? undefined : false }),
    });
  };

  // Update URL when online-only toggle changes
  const setShowOnlineOnly = (value: boolean) => {
    navigate({
      to: '/persistent-streams',
      search: buildSearchParams({ clientID, search: searchFilter || undefined, onlineOnly: value ? undefined : false }),
    });
  };

  // Get client name for the filter badge
  const filteredClient = clientID ? clients.find(c => c.clientID === clientID) : null;

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = await getAccessTokenSilently();
        await Promise.all([
          fetchStreams(token, logout),
          clients.length === 0 ? fetchClients(token, logout) : Promise.resolve(),
        ]);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    loadData();
  }, [getAccessTokenSilently, fetchStreams, fetchClients, clients.length]);

  // Merge static stream data with real-time WSS data
  const filteredStreams: PersistentStreamWithWSS[] = useMemo(() => {
    // Merge WSS data with streams
    let merged: PersistentStreamWithWSS[] = streams.map((stream) => ({
      ...stream,
      wss: wssEncoders.get(stream.encoderID) || null,
    }));

    // Filter by clientID if provided
    if (clientID) {
      merged = merged.filter((stream) => stream.clientID === clientID);
    }

    // Filter by online status (check both WSS data and DB status)
    if (showOnlineOnly) {
      merged = merged.filter((stream) => {
        // If we have WSS data, use it
        if (stream.wss) return stream.wss.online;
        // Fall back to DB status (1 = online)
        return stream.status === 1;
      });
    }

    // Filter by search text
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      merged = merged.filter((stream) =>
        (stream.title?.toLowerCase() || '').includes(search) ||
        stream.clientName.toLowerCase().includes(search) ||
        stream.encoderID.toLowerCase().includes(search)
      );
    }

    return merged;
  }, [streams, wssEncoders, clientID, searchFilter, showOnlineOnly]);

  const table = useReactTable({
    data: filteredStreams,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  const handleRefresh = async () => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE_URL}/api/cache/invalidate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key: 'persistent-streams:all' }),
      });
      invalidateCache();
      await fetchStreams(token, logout, true);
    } catch (err) {
      console.error('Error refreshing persistent streams:', err);
    }
  };

  const handleExportCSV = () => {
    const headers = ['Client', 'Title', 'Encoder ID', 'Status', 'Resolution', 'Bitrate', 'FPS', 'Live Stream URI', 'Last Communication'];
    const rows = filteredStreams.map((stream) => {
      const streamInfo = getStreamInfo(stream);
      const status = stream.wss
        ? (stream.wss.online ? 'Online' : 'Offline')
        : (stream.status === 1 ? 'Online' : stream.status === 0 ? 'Offline' : stream.status === 2 ? 'Streaming' : '-');
      const resolution = streamInfo?.width && streamInfo?.height
        ? formatResolution(streamInfo.width, streamInfo.height)
        : '-';
      const bitrate = streamInfo?.bitrate ? formatBitrate(streamInfo.bitrate) : '-';
      const fps = streamInfo?.frameRate ? formatFrameRate(streamInfo.frameRate) : '-';

      return [
        stream.clientName,
        stream.title || stream.encoderID,
        stream.encoderID,
        status,
        resolution,
        bitrate,
        fps,
        stream.LiveStreamURI || '-',
        stream.lastCommunication ? new Date(stream.lastCommunication).toLocaleString() : '-',
      ];
    });

    const date = new Date().toISOString().split('T')[0];
    downloadCSV(`persistent_streams_${date}.csv`, headers, rows);
  };

  const clearClientFilter = () => {
    navigate({
      to: '/persistent-streams',
      search: buildSearchParams({ search: searchFilter || undefined, onlineOnly: showOnlineOnly ? undefined : false }),
    });
  };

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h3 className="text-2xl font-semibold">Persistent Streams</h3>
              {filteredClient && (
                <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm">
                  <span>{filteredClient.name}</span>
                  <button
                    onClick={clearClientFilter}
                    className="ml-1 hover:bg-accent rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            <CacheTimestamp cachedAt={cacheInfo?.cachedAt ?? null} />
          </div>

          {loading && <p>Loading persistent streams...</p>}

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          {!loading && !error && streams.length === 0 && (
            <p>No persistent streams found.</p>
          )}

          {!loading && !error && streams.length > 0 && (
            <>
              <div className="flex justify-end items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="online-only"
                    checked={showOnlineOnly}
                    onCheckedChange={setShowOnlineOnly}
                  />
                  <Label htmlFor="online-only" className="cursor-pointer">
                    Online Only
                  </Label>
                </div>
                <Input
                  placeholder="Filter by title, client, or encoder ID..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-80"
                />
                <Button
                  variant="outline"
                  onClick={handleExportCSV}
                  disabled={filteredStreams.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Force Refresh
                </Button>
                <ResetFiltersButton
                  routePath="/persistent-streams"
                  defaultValues={{ search: undefined, onlineOnly: true }}
                />
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        onClick={() => navigate({ to: `/encoders/${row.original.encoderID}` })}
                        className="cursor-pointer hover:bg-accent"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Showing {filteredStreams.length} of {streams.length} persistent streams
              </p>
            </>
          )}

        </section>
      </div>
    </Layout>
  );
}
